import { router } from "..";
import {
	getSmartplug,
	putSmartplugPowerEnabled,
} from "../api/smartplugs_{imei}";
import { prices } from "../billing/prices";
import { deviceCard, formatSubscriptionStatus } from "../device/device_card";
import { errorSection } from "../error-section";
import { loadingScreen } from "../loading-screen";
import { relativeTimeString, t } from "../locale";
import { addNotificationListener } from "../mobile";
import { state } from "../state";
import { keyValueParagraph } from "../view/components";
import { Html } from "../view/html";
import { MaterialDesign } from "../view/material_design";
import { getSmartplugWarnings } from "./sp_warnings";

/**
 * @param {Smartplug} sp
 */
async function formatSubscriptionPlan(sp) {
	if (sp.Subscription.Trialing) return t.trial;

	const { SmartplugMonth, SmartplugYear } = await prices;

	switch (sp.Subscription.PriceIds.Current) {
		case SmartplugMonth.Id:
			return t.monthly;
		case SmartplugYear.Id:
			return t.yearly;
	}

	const expiryTime = new Date(sp.Subscription.ExpiresAt);
	if (Date.now() < expiryTime.getTime()) return "";

	return t.none;
}

/**
 * @param {Smartplug} sp
 */
async function smartplugCard(sp) {
	addNotificationListener("smartplug", sp.Imei, () => {
		getSmartplug(sp.Imei).then((sp) => {
			state.smartplugs[sp.Imei] = sp;
		});
	});

	const imei = sp.Imei;
	const togglePower = () => {
		const attempt = errorSection.attempt(async () => {
			const sp = await putSmartplugPowerEnabled(
				imei,
				!state.smartplugs[imei].PowerEnabled,
			);

			state.smartplugs[sp.Imei] = sp;

			if (sp.PowerEnabled) {
				powerOffIcon.replaceWith(powerOnIcon);
			} else {
				powerOnIcon.replaceWith(powerOffIcon);
			}
		});
		loadingScreen.waitFor(attempt);
	};

	const powerOnIcon = MaterialDesign.icon({
		id: "flash_on",
		onClick: togglePower,
	});

	const powerOffIcon = MaterialDesign.icon({
		id: "flash_off",
		onClick: togglePower,
	});

	return deviceCard({
		deviceName: sp.Metadata.Name,
		deviceType: "smartplug",
		productName: "SmartPlug",
		warnings: getSmartplugWarnings(sp),
		lines: [
			keyValueParagraph(t.device.card.statusLabel),
			keyValueParagraph(
				`- ${t.smartplug.card.powerLabel}`,
				sp.PowerEnabled ? t.on : t.off,
			),
			keyValueParagraph(
				`- ${t.smartplug.powerLoadLabel}`,
				`${sp.Load} W`,
			),
			keyValueParagraph(
				`- ${t.smartplug.consumptionLabel}`,
				`${sp.Consumption} Wh`,
			),
			keyValueParagraph(
				`- ${t.smartplug.temperatureLabel}`,
				sp.Temperature >= -20 ? `${sp.Temperature} °C` : "-",
			),
			keyValueParagraph(`- ${t.signalStrength}`, `${sp.SignalStrength}%`),
			keyValueParagraph(
				`- ${t.lastActive}`,
				relativeTimeString(sp.LastUplinkAt),
			),
			keyValueParagraph(t.device.subscriptionLabel),
			keyValueParagraph(
				`- ${t.device.card.planLabel}`,
				await formatSubscriptionPlan(sp),
			),
			keyValueParagraph(...formatSubscriptionStatus(sp.Subscription)),
		],
		buttons: [
			Html.anchor({}, sp.PowerEnabled ? powerOnIcon : powerOffIcon),
			Html.anchor(
				{},
				MaterialDesign.icon({
					id: "monitoring",
					onClick: () => router.jump(`/smartplug/${sp.Imei}/logs`),
				}),
			),
			Html.anchor(
				{},
				MaterialDesign.icon({
					id: "settings",
					onClick: () => router.jump(`/smartplug/${sp.Imei}`),
				}),
			),
		],
	});
}

export { smartplugCard };
